// Place search via postcodes.io (no key, CORS open, UK only):
//   full postcode  → /postcodes/{pc}
//   outward code   → /outcodes/{outcode}       e.g. "BS1"
//   anything else  → /places?q=                 OS Open Names: towns, villages, suburbs

const API = 'https://api.postcodes.io';

export interface PlaceResult {
  label: string;
  detail: string;
  lat: number;
  lon: number;
  /** Suggested zoom: tighter for a postcode than for a town. */
  zoom: number;
}

const FULL_POSTCODE = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
const OUTCODE = /^[A-Z]{1,2}[0-9][A-Z0-9]?$/i;

export type QueryKind = 'postcode' | 'outcode' | 'place';

export function classifyQuery(query: string): QueryKind {
  const q = query.trim();
  if (FULL_POSTCODE.test(q)) return 'postcode';
  if (OUTCODE.test(q)) return 'outcode';
  return 'place';
}

interface PostcodeResponse {
  result: {
    postcode: string;
    latitude: number | null;
    longitude: number | null;
    admin_district: string | null;
    country: string;
  } | null;
}
interface OutcodeResponse {
  result: {
    outcode: string;
    latitude: number | null;
    longitude: number | null;
    admin_district: string[] | null;
  } | null;
}
interface PlacesResponse {
  result:
    | {
        name_1: string;
        local_type: string;
        county_unitary: string | null;
        district_borough: string | null;
        region: string | null;
        latitude: number;
        longitude: number;
      }[]
    | null;
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T | null> {
  const response = await fetch(url, signal ? { signal } : {});
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Search failed (${response.status})`);
  return (await response.json()) as T;
}

const PLACE_ZOOM: Record<string, number> = {
  City: 12,
  Town: 13,
  Village: 14,
  Hamlet: 15,
  'Suburban Area': 14,
  'Other Settlement': 14,
};

export async function searchPlaces(query: string, signal?: AbortSignal): Promise<PlaceResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const kind = classifyQuery(q);

  if (kind === 'postcode') {
    const body = await getJson<PostcodeResponse>(
      `${API}/postcodes/${encodeURIComponent(q)}`,
      signal,
    );
    const r = body?.result;
    if (!r || r.latitude === null || r.longitude === null) return [];
    return [
      {
        label: r.postcode,
        detail: [r.admin_district, r.country].filter(Boolean).join(', '),
        lat: r.latitude,
        lon: r.longitude,
        zoom: 16,
      },
    ];
  }

  if (kind === 'outcode') {
    const body = await getJson<OutcodeResponse>(`${API}/outcodes/${encodeURIComponent(q)}`, signal);
    const r = body?.result;
    if (r && r.latitude !== null && r.longitude !== null) {
      return [
        {
          label: r.outcode,
          detail: (r.admin_district ?? []).slice(0, 2).join(', ') || 'Postcode district',
          lat: r.latitude,
          lon: r.longitude,
          zoom: 14,
        },
      ];
    }
    // Not a real outcode; fall through to place names ("A1" could be a road, "Ely" a town).
  }

  const body = await getJson<PlacesResponse>(
    `${API}/places?q=${encodeURIComponent(q)}&limit=6`,
    signal,
  );
  return (body?.result ?? []).map((p) => ({
    label: p.name_1,
    detail: [p.local_type, p.district_borough ?? p.county_unitary ?? p.region]
      .filter(Boolean)
      .join(' · '),
    lat: p.latitude,
    lon: p.longitude,
    zoom: PLACE_ZOOM[p.local_type] ?? 14,
  }));
}
